import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Modal, Nav, Tab } from "react-bootstrap";
import "../addfacility/addfacility.css";
import CalenderIcon from "../../../assets/svgs/calender.svg";
import "react-datepicker/dist/react-datepicker.css"; // DatePicker styles
import DatePicker from "react-datepicker";
import plus from "../../../assets/svgs/plussqr.svg";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from 'react-redux';
import { addFacility, resetFacilityState } from '../../../redux/slices/AddFacilitySlice';
import { getAllSports } from '../../../services/api';
import { EditNewFacility } from '../../../services/api';
import {
  fetchSports,
  openModal,
  closeModal,
  selectSport,
  removeSport,
  clearSelectedSports,
  setActiveTab,
  getAllSelectedSports
} from '../../../redux/slices/getSportsSlice';
import { fetchVenueFacilities, selectFacility, unselectFacility, clearSelectedFacilities, setSelectedFacilities } from '../../../redux/slices/venueFacilitiesSlice';
import { CircularProgress } from '@mui/material';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import PlacesAutocomplete from "../../PlacesAutocomplete";

// Facility icons
import sportIcons from "../../facilities/addfacility/sportsicon";
import cross from "../../../assets/sportsicon/cross.svg"
import { FaKey, FaDumbbell, FaCoffee, FaLock, FaToilet, FaShower, FaParking, FaWifi } from 'react-icons/fa';
// Venue facilities data - move this before the component
// const venueFacilities = [
//   { id: 1, title: 'Password', icon: password },
//   { id: 2, title: 'Equipment Provided', icon: equipment },
//   { id: 3, title: 'Refreshments', icon: refreshment },
//   { id: 4, title: 'Lockers', icon: lockers },
//   { id: 5, title: 'Toilet', icon: toilet },
//   { id: 6, title: 'Shower', icon: shower },
//   { id: 7, title: 'Parking', icon: parking },
//   { id: 8, title: 'Internet', icon: internet },
// ];

const chunkArray = (array, n) => {
  return Array.from({ length: Math.ceil(array.length / n) }, (_, i) =>
    array.slice(i * n, i * n + n)
  );
};

// List of Indoor & Outdoor Sports
const indoorSports = ["E-sports", "Gyming", "Snooker", "Bowling", "Table Tennis", "Yoga", "Poker"];
const outdoorSports = ["Basketball", "Football", "Tennis", "Golf", "Cricket", "Hockey", "Volleyball", "American Football", "Badminton", "Skating"];

const indoorSportPairs = chunkArray(indoorSports, 2);
const outdoorSportPairs = chunkArray(outdoorSports, 2);

// Add this constant at the top of the file, after imports
const EDITABLE_FIELDS = {
  name: true,
  location: true,
  phone: true,
  email: true,
  socialLinks: true,
  venueTimings: true,
  terms: true,
  startingFrom: true,
  contractTenure: false, // Contract tenure is managed by admin, not editable in edit mode
  venueFacility: true,
  availableSport: true,
  coverPhoto: true,
  bankDetails: true
};

const AddFacility = ({ isEditMode = false, isReadOnly = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const facilityData = location.state?.facility || {};

  // Basic info state
  const [facilityName, setFacilityName] = useState(isEditMode ? facilityData.name || "" : "");
  const [facilityLocation, setFacilityLocation] = useState(isEditMode ? (facilityData.location?.name || facilityData.location?.address || "") : "");
  const [contactNumber, setContactNumber] = useState(isEditMode ? facilityData.phone || "" : "");
  const [email, setEmail] = useState(isEditMode ? facilityData.email || "" : "");
  const [facebook, setFacebook] = useState(isEditMode ? facilityData.socialLinks?.facebook || "" : "");
  const [instagram, setInstagram] = useState(isEditMode ? facilityData.socialLinks?.instagram || "" : "");

  // Place selection state
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Timing and contract state - initialize empty first and fill with useEffect
  const [venueTimings, setVenueTimings] = useState([{ day: "", from: "", to: "" }]);
  const [contractStartDate, setContractStartDate] = useState(
    isEditMode && facilityData.contractTenure?.from
      ? new Date(facilityData.contractTenure.from)
      : null
  );
  const [contractEndDate, setContractEndDate] = useState(
    isEditMode && facilityData.contractTenure?.to
      ? new Date(facilityData.contractTenure.to)
      : null
  );
  const [startingPrice, setStartingPrice] = useState(isEditMode ? facilityData.startingFrom || "" : "");
  const [termsAndConditions, setTermsAndConditions] = useState(isEditMode ? facilityData.terms || "" : "");

  // Bank details state
  const [bankName, setBankName] = useState(isEditMode ? facilityData.bankDetails?.bankName || "" : "");
  const [accountNumber, setAccountNumber] = useState(isEditMode ? facilityData.bankDetails?.accountNumber || "" : "");
  const [accountHolderName, setAccountHolderName] = useState(isEditMode ? facilityData.bankDetails?.accountHolderName || "" : "");
  const [iban, setIban] = useState(isEditMode ? facilityData.bankDetails?.iban || "" : "");
  const [ntn, setNtn] = useState(isEditMode ? facilityData.bankDetails?.ntn || "" : "");

  // Facility ID for editing
  const [facilityId, setFacilityId] = useState(isEditMode ? facilityData._id || "" : "");

  // Images state
  const [images, setImages] = useState(isEditMode ?
    [facilityData.coverPhoto, ...(facilityData.images || [])] : []
  );

  // Sport selection state
  const [selectedSports, setSelectedSports] = useState([]);
  const [showSportModal, setShowSportModal] = useState(false);

  // Venue facilities state
  const [selectedFacilityNames, setSelectedFacilityNames] = useState([]);

  // Add this state to track if images have changed
  const [imagesChanged, setImagesChanged] = useState(false);

  // Add state to track deleted and new images
  const [deletedImages, setDeletedImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]);

  // Get sports data and state from Redux
  const dispatch = useDispatch();
  const { sports, selectedSports: reduxSelectedSports, isModalOpen, loading: sportsLoading } = useSelector((state) => ({
    sports: state.sports.sports || { indoor: [], outdoor: [] },
    selectedSports: state.sports.selectedSports || { indoor: [], outdoor: [] },
    isModalOpen: state.sports.isModalOpen,
    loading: state.sports.loading
  }));
  const { loading: addFacilityLoading, error, success } = useSelector((state) => state.addFacility);

  // Add this state at the top with your other states
  const [isLoading, setIsLoading] = useState(false);

  // Add this state for preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);

  // Get venue facilities from Redux
  const { facilities: venueFacilities, selectedFacilities, loading: facilitiesLoading } = useSelector((state) => ({
    facilities: state.venueFacilities?.facilities || [],
    selectedFacilities: state.venueFacilities?.selectedFacilities || [],
    loading: state.venueFacilities?.loading || false
  }));

  // Add new state for cover photo
  const [coverPhoto, setCoverPhoto] = useState(isEditMode ? facilityData.coverPhoto || "" : "");

  // Fetch venue facilities on component mount
  useEffect(() => {
    dispatch(fetchVenueFacilities());
  }, [dispatch]);

  // Add cleanup useEffect at the top level of the component
  useEffect(() => {
    // Clear selected sports when component mounts
    if (!isEditMode) {
      console.log("Clearing selected sports for add mode");
      dispatch(clearSelectedSports());
      dispatch(clearSelectedFacilities());
    }

    // Cleanup function to clear sports and facilities when component unmounts
    return () => {
      console.log("Component unmounting - clearing selections");
      dispatch(clearSelectedSports());
      dispatch(clearSelectedFacilities());
    };
  }, [dispatch, isEditMode]);

  // Update useEffect for loading venue facilities in edit mode
  useEffect(() => {
    if (isEditMode && facilityData?.venueFacility) {

      // Clear previous selections
      dispatch(clearSelectedFacilities());

      // Map facility data to IDs and select them
      facilityData.venueFacility.forEach(facility => {
        const facilityId = typeof facility === 'object' ? facility.id : facility;
        const matchedFacility = venueFacilities.find(f =>
          f._id === facilityId ||
          f.name.toLowerCase() === (typeof facility === 'string' ? facility : facility.name).toLowerCase()
        );
        if (matchedFacility) {
          dispatch(selectFacility(matchedFacility._id));
        }
      });
    }
  }, [isEditMode, facilityData, venueFacilities, dispatch]);

  // Update handleSelect function
  const handleSelect = (facilityId) => {
    const isSelected = selectedFacilities.includes(facilityId);

    if (isSelected) {
      dispatch(unselectFacility(facilityId));
    } else {
      dispatch(selectFacility(facilityId));
    }

    // Get the updated selected facilities after state change
    const updatedFacilities = isSelected
      ? selectedFacilities.filter(id => id !== facilityId)
      : [...selectedFacilities, facilityId];

    // Map facility IDs to full facility objects for the payload
    const selectedFacilityObjects = updatedFacilities.map(id => {
      const facility = venueFacilities.find(f => f._id === id);
      return facility ? {
        id: facility._id,
        name: facility.name,
        logo: facility.logo
      } : null;
    }).filter(facility => facility !== null);

    // Update selectedFacilityNames with facility names
    setSelectedFacilityNames(selectedFacilityObjects.map(f => f.name));

  };

  // Update isFacilitySelected function
  const isFacilitySelected = (facility) => {
    return selectedFacilities.includes(facility._id);
  };

  // Format date as MM-DD-YYYY
  const formatDateForPayload = (date) => {
    if (!date) return "";
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // Format time from 24h to 12h with AM/PM
  const formatTimeToAMPM = (time) => {
    if (!time) return "";

    try {
      // Ensure time is in HH:MM format
      const [hours, minutes] = time.split(':').map(Number);

      // Handle invalid values
      if (isNaN(hours) || isNaN(minutes)) {
        throw new Error('Invalid time format');
      }

      // Convert to 12-hour format
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;

      // Format the time
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      console.error('Error formatting time to AM/PM:', error, time);
      return "";
    }
  };

  // Format time from 12h (AM/PM) to 24h format for input fields
  const formatTimeFrom12To24 = (timeString) => {
    if (!timeString) return "";


    // If it's already in 24h format (no AM/PM)
    if (!timeString.includes('AM') && !timeString.includes('PM')) {
      // Check if it matches HH:MM format
      if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString)) {
        return timeString.padStart(5, '0'); // Ensure HH:MM format
      }
    }

    try {
      // Remove any extra spaces
      timeString = timeString.trim();

      // Split time and period
      let [time, period] = timeString.split(/\s+/);
      period = (period || '').toUpperCase();

      // Split hours and minutes
      let [hours, minutes] = time.split(':').map(num => parseInt(num, 10));

      // Handle missing or invalid values
      if (isNaN(hours)) hours = 0;
      if (isNaN(minutes)) minutes = 0;

      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      // Format as HH:MM
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Error parsing time:', error, timeString);
      return '';
    }
  };

  // Update the useEffect for loading sports in edit mode
  useEffect(() => {
    if (isEditMode && facilityData?.availableSport && Array.isArray(facilityData.availableSport)) {

      // Fetch sports data first if not already loaded
      if (!(sports.indoor?.length) && !(sports.outdoor?.length)) {
    
        dispatch(fetchSports())
          .then(() => {
            // Process each sport from facility data after sports are loaded
            processSportsSelection();
          })
          .catch(error => {
            console.error("Error loading sports:", error);
            toast.error("Failed to load sports data");
          });
      } else {
        // If sports are already loaded, just process the selection
        processSportsSelection();
      }
    }
  }, [isEditMode, facilityData?.availableSport, dispatch]);

  // Add new function to process sports selection
  const processSportsSelection = () => {

    // Check if sports data is available
    if (!sports) {
      console.log("Sports data not available yet, skipping selection");
      return;
    }

    // Clear any previously selected sports
    dispatch(clearSelectedSports());

    // Process each sport from facility data
    if (Array.isArray(facilityData.availableSport)) {
      console.log("Processing availableSport array:", facilityData.availableSport);

      facilityData.availableSport.forEach((sport, index) => {
        console.log(`Processing sport ${index}:`, sport);

        // Handle both object and string formats
        if (typeof sport === 'object' && sport._id) {
          // If sport is an object with _id and type, use it directly
          console.log("Selecting sport from facility data:", sport.name, sport._id, "type:", sport.type);
          dispatch(selectSport({ sportId: sport._id, type: sport.type }));

          // Add a small delay to see the state update
          setTimeout(() => {
            console.log("Redux state after selection:", reduxSelectedSports);
          }, 100);
        } else {
          // If sport is just an ID string, find it in the sports array
          const sportId = sport;
          const sportData = [...(sports.indoor || []), ...(sports.outdoor || [])].find(s => s._id === sportId);

          if (sportData) {
            console.log("Selecting sport from sports array:", sportData.name, sportData._id, "type:", sportData.type);
            dispatch(selectSport({ sportId: sportData._id, type: sportData.type }));
          } else {
            console.log("Sport not found in sports array:", sportId);
          }
        }
      });
    } else {
      console.log("availableSport is not an array:", facilityData.availableSport);
    }
  };

  // Enhanced handleOpenSportModal function
  const handleOpenSportModal = async () => {

    try {
      // Always fetch fresh sports data
      const result = await dispatch(fetchSports()).unwrap();
  
      if (isEditMode) {
        console.log("Edit mode - current selected sports:", reduxSelectedSports);
      }

      // Open modal after data is loaded
      dispatch(openModal());
    } catch (error) {
      console.error("Error fetching sports:", error);
      toast.error("Failed to load sports. Please try again.");
    }
  };

  // Enhanced handleSelectSport function
  const handleSelectSport = (sportId) => {

    // Update both Redux and local state
    dispatch(selectSport(sportId));
    setSelectedSports(prev => {
      const isAlreadySelected = prev.includes(sportId);
      if (isAlreadySelected) {
        return prev.filter(id => id !== sportId);
      } else {
        return [...prev, sportId];
      }
    });

    // Verify selection after a short delay
    setTimeout(() => {
      console.log("Updated selected sports:", selectedSports);
    }, 100);
  };

  // Update useEffect to store original images
  useEffect(() => {
    if (isEditMode && facilityData) {
      // Combine cover photo with images array
      const allImages = [
        facilityData.coverPhoto,
        ...(facilityData.images || [])
      ].filter(img => img); // Filter out any null/undefined values

      setOriginalImages(allImages);
      setImages(allImages);
      setDeletedImages([]);
      setNewImages([]);
    }
  }, [isEditMode, facilityData]);

  // Update handleImageUpload function
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);

    // Check if adding these files would exceed the 5 image limit
    // For cover replacement, we don't need to check this since we're replacing, not adding
    const isCoverReplacement = event.target.getAttribute('data-type') === 'cover';
    if (!isCoverReplacement && images.length + files.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    // Check file size and type for all files
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      if (!file.type.match('image.*')) {
        toast.error("Please select an image file");
        return;
      }
    }

    try {
      // Convert files to base64
      const processedImages = await Promise.all(
        files.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );


      if (isEditMode) {
        // In edit mode
        if (isCoverReplacement) {
          // If replacing cover photo and it's an existing image, add it to deletedImages
          const currentCoverPhoto = images[0];
          if (currentCoverPhoto && (currentCoverPhoto.startsWith('http') || currentCoverPhoto.startsWith('/facility'))) {
            setDeletedImages(prev => [...prev, currentCoverPhoto]);
          }
          // Replace the cover photo (first image)
          setImages(prevImages => {
            const newImages = [processedImages[0], ...prevImages.slice(1)];
            return newImages;
          });
        } else {
          // Adding additional images
          setImages(prevImages => {
            const newImages = [...prevImages];
            processedImages.forEach(img => {
              if (newImages.length < 5) {
                newImages.push(img);
                // Track new uploads
                setNewImages(prev => [...prev, img]);
              }
            });
            return newImages;
          });
        }
      } else {
        // In add mode
        if (images.length === 0 || isCoverReplacement) {
          // First image becomes cover or replace existing cover
          setImages(prevImages => {
            const newImages = [processedImages[0], ...prevImages.slice(1)];
            return newImages;
          });
        } else {
          // Add additional images
          setImages(prevImages => {
            const newImages = [...prevImages];
            processedImages.forEach(img => {
              if (newImages.length < 5) {
                newImages.push(img);
              }
            });
            return newImages;
          });
        }
      }

      setImagesChanged(true);
      toast.success("Image(s) uploaded successfully");
    } catch (error) {
      console.error('Error processing images:', error);
      toast.error("Error processing images. Please try again.");
    }
  };

  // Update removeImage function
  const removeImage = (index) => {
    // In edit mode, don't allow deleting cover photo (index 0)
    if (isEditMode && index === 0) {
      toast.info("Cover photo can be replaced but not deleted");
      return;
    }

    setImages(prevImages => {
      const newImages = [...prevImages];
      const removedImage = newImages[index];

      // In edit mode, check if the removed image is an existing one (not a new upload)
      if (isEditMode && (removedImage.startsWith('http') || removedImage.startsWith('/facility'))) {
        setDeletedImages(prev => [...prev, removedImage]);
      }

      newImages.splice(index, 1);
      setImagesChanged(true);
      return newImages;
    });
  };

  // Update the addVenueTiming function to copy values from the last timing
  const addVenueTiming = () => {
    // If there are existing timings, copy the values from the last one
    if (venueTimings.length > 0) {
      const lastTiming = venueTimings[venueTimings.length - 1];
      setVenueTimings([...venueTimings, {
        day: lastTiming.day,
        from: lastTiming.from,
        to: lastTiming.to
      }]);
    } else {
      // If there are no existing timings, add an empty one
      setVenueTimings([...venueTimings, { day: "", from: "", to: "" }]);
    }
  };

  // Add function to remove a venue timing
  const removeVenueTiming = (indexToRemove) => {
    // Don't allow removing the last timing
    if (venueTimings.length <= 1) {
      toast.error("At least one venue timing is required");
      return;
    }

    setVenueTimings(venueTimings.filter((_, index) => index !== indexToRemove));
  };

  // Handle venue timing handlers
  const handleTimingChange = (index, field, value) => {
    console.log(`Updating timing - index: ${index}, field: ${field}, value: ${value}`);

    const updatedTimings = [...venueTimings];

    if (field === 'from' || field === 'to') {
      // For time fields, ensure proper format
      if (value) {
        const formattedTime = value.match(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)
          ? value
          : formatTimeFrom12To24(value);
        updatedTimings[index][field] = formattedTime;
      } else {
        updatedTimings[index][field] = '';
      }
    } else {
      // For day field
      updatedTimings[index][field] = value;
    }

    console.log("Updated timings:", updatedTimings);
    setVenueTimings(updatedTimings);
  };

  // Update the resetFormData function to ensure it clears sports
  const resetFormData = () => {
    setFacilityName("");
    setFacilityLocation("");
    setContactNumber("");
    setEmail("");
    setFacebook("");
    setInstagram("");
    setVenueTimings([{ day: "", from: "", to: "" }]);
    setContractStartDate(null);
    setContractEndDate(null);
    setStartingPrice("");
    setTermsAndConditions("");
    setBankName("");
    setAccountNumber("");
    setAccountHolderName("");
    setIban("");
    setNtn("");
    setImages([]);
    setCoverPhoto("");
    setSelectedFacilityNames([]);
    setDeletedImages([]);
    setNewImages([]);
    setImagesChanged(false);
    dispatch(clearSelectedSports());
    console.log("Form data reset, including images");
  };

  // Add this function to check if end date is after start date
  const isEndDateValid = () => {
    return contractEndDate >= contractStartDate;
  };

  // Handle place selection from Google Places API
  const handlePlaceSelect = (placeData) => {
    setSelectedPlace(placeData);
  };

  // Update handleEndDateChange to validate that end date is after start date
  const handleContractEndDateChange = (date) => {
    if (date < contractStartDate) {
      toast.error("End date cannot be before start date");
      return;
    }
    setContractEndDate(date);
  };

  // Update isFormValid to handle edit mode and images properly
  const isFormValid = () => {
    // Basic validation for required fields
    if (!facilityName || !facilityLocation || !contactNumber) {
      return false;
    }

    // Validate contact number for Pakistan (+92, 10 digits) or UAE (+971, 9 digits)
    const contactNumberRegex = /^\+(92\d{10}|971\d{9})$/;
    if (!contactNumberRegex.test(contactNumber)) {
      return false;
    }

    // Validate email if provided
    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return false;
      }
    }

    // Validate venue timings
    if (venueTimings.length === 0) {
      return false;
    }

    // Check if all venue timings are properly filled
    const isTimingsValid = venueTimings.every(timing =>
      timing.day && timing.from && timing.to
    );
    if (!isTimingsValid) {
      return false;
    }

    // // Validate contract dates
    // if (!contractStartDate || !contractEndDate || contractEndDate < contractStartDate) {
    //   return false;
    // }

    // Validate starting price and terms
    if (!startingPrice || !termsAndConditions) {
      return false;
    }

    // Validate venue facilities
    if (selectedFacilities.length === 0) {
      return false;
    }

    // Validate sports selection
    const totalSelectedSports = (reduxSelectedSports.indoor?.length || 0) + (reduxSelectedSports.outdoor?.length || 0);
    if (totalSelectedSports < 1) {
      return false;
    }

    // Validate images
    if (images.length === 0) {
      return false;
    }

    // In edit mode, check if there are any changes
    if (isEditMode) {
      const changedFields = getChangedFields();
      return Object.keys(changedFields).length > 0 || imagesChanged;
    }

    return true;
  };

  // Update getChangedFields to include deletedImages
  const getChangedFields = () => {
    if (!isEditMode || !facilityData) return {};

    const changedFields = {};
    let hasChanges = false;

    // Handle images separately to ensure proper format
    if (imagesChanged) {
      // First image is always cover photo
      if (images[0]) {
        changedFields.coverPhoto = images[0];
        hasChanges = true;
      }
      // Rest are additional images
      if (images.length > 1) {
        changedFields.images = images.slice(1);
        hasChanges = true;
      } else {
        changedFields.images = []; // Empty array if no additional images
      }

      // Add deletedImages to the payload if there are any
      if (deletedImages.length > 0) {
        changedFields.deletedImages = deletedImages;
        hasChanges = true;
      }
    }

    // Only check editable fields
    if (EDITABLE_FIELDS.name && facilityName.toLowerCase() !== (facilityData.name || "").toLowerCase()) {
      changedFields.name = facilityName.toLowerCase();
      hasChanges = true;
    }

    if (EDITABLE_FIELDS.location && facilityLocation !== (facilityData.location?.name || "")) {
      changedFields.location = {
        name: facilityLocation,
        lat: selectedPlace?.geometry?.location?.lat() || 24.8607,
        lng: selectedPlace?.geometry?.location?.lng() || 67.0011
      };
      hasChanges = true;
    }

    if (EDITABLE_FIELDS.phone && contactNumber !== (facilityData.phone || "")) {
      changedFields.phone = contactNumber;
      hasChanges = true;
    }

    if (EDITABLE_FIELDS.email && email !== (facilityData.email || "")) {
      changedFields.email = email;
      hasChanges = true;
    }

    if (EDITABLE_FIELDS.socialLinks) {
      const hasSocialLinksChanged =
        facebook !== (facilityData.socialLinks?.facebook || "") ||
        instagram !== (facilityData.socialLinks?.instagram || "");

      if (hasSocialLinksChanged) {
        changedFields.socialLinks = { facebook, instagram };
        hasChanges = true;
      }
    }

    if (EDITABLE_FIELDS.startingFrom && startingPrice !== (facilityData.startingFrom || "")) {
      changedFields.startingFrom = startingPrice;
      hasChanges = true;
    }

    if (EDITABLE_FIELDS.terms && termsAndConditions !== (facilityData.terms || "")) {
      changedFields.terms = termsAndConditions;
      hasChanges = true;
    }

    if (EDITABLE_FIELDS.venueFacility) {
      const selectedFacilityObjects = selectedFacilities.map(id => {
        const facility = venueFacilities.find(f => f._id === id);
        return facility ? {
          id: facility._id,
          name: facility.name,
          logo: facility.logo
        } : null;
      }).filter(facility => facility !== null);

      const originalFacilities = Array.isArray(facilityData.venueFacility)
        ? facilityData.venueFacility.map(f => typeof f === 'object' ? f.id : f)
        : [];

      if (JSON.stringify([...originalFacilities].sort()) !== JSON.stringify(selectedFacilityObjects.map(f => f.id).sort())) {
        changedFields.venueFacility = selectedFacilityObjects;
        hasChanges = true;
        console.log('Venue Facilities Changed:', {
          original: originalFacilities,
          updated: selectedFacilityObjects
        });
      }
    }

    if (EDITABLE_FIELDS.contractTenure) {
      const originalStartDate = facilityData.contractTenure?.from
        ? new Date(facilityData.contractTenure.from)
        : null;
      const originalEndDate = facilityData.contractTenure?.to
        ? new Date(facilityData.contractTenure.to)
        : null;

      if (
        !originalStartDate ||
        !originalEndDate ||
        originalStartDate.getTime() !== contractStartDate.getTime() ||
        originalEndDate.getTime() !== contractEndDate.getTime()
      ) {
        changedFields.contractTenure = {
          from: formatDateForPayload(contractStartDate),
          to: formatDateForPayload(contractEndDate)
        };
        hasChanges = true;
      }
    }

    if (EDITABLE_FIELDS.venueTimings) {
      const originalTimings = facilityData.venueTimings || [];
      const currentTimings = venueTimings.map(timing => ({
        day: timing.day,
        startTime: formatTimeToAMPM(timing.from),
        endTime: formatTimeToAMPM(timing.to)
      }));

      if (JSON.stringify(originalTimings) !== JSON.stringify(currentTimings)) {
        changedFields.venueTimings = currentTimings;
        hasChanges = true;
      }
    }

    if (EDITABLE_FIELDS.availableSport) {
      const originalSports = Array.isArray(facilityData.availableSport)
        ? facilityData.availableSport.map(sport =>
          typeof sport === 'object' && sport._id ? sport._id : sport
        )
        : [];

      const currentSports = [...(reduxSelectedSports.indoor || []), ...(reduxSelectedSports.outdoor || [])];

      if (originalSports.length !== currentSports.length ||
        JSON.stringify([...originalSports].sort()) !== JSON.stringify([...currentSports].sort())) {
        changedFields.availableSport = currentSports;
        hasChanges = true;
      }
    }

    if (EDITABLE_FIELDS.bankDetails) {
      const originalBankDetails = facilityData.bankDetails || {};
      const currentBankDetails = {
        bankName: bankName || null,
        accountNumber: accountNumber || null,
        accountHolderName: accountHolderName || null,
        iban: iban || null,
        ntn: ntn || null
      };

      const hasBankDetailsChanged =
        originalBankDetails.bankName !== currentBankDetails.bankName ||
        originalBankDetails.accountNumber !== currentBankDetails.accountNumber ||
        originalBankDetails.accountHolderName !== currentBankDetails.accountHolderName ||
        originalBankDetails.iban !== currentBankDetails.iban ||
        originalBankDetails.ntn !== currentBankDetails.ntn;

      if (hasBankDetailsChanged) {
        changedFields.bankDetails = currentBankDetails;
        hasChanges = true;
      }
    }

    // Always include _id in edit mode
    if (isEditMode && hasChanges) {
      changedFields._id = facilityId;
    }

    console.log("Changed fields with images:", changedFields);
    return hasChanges ? changedFields : {};
  };

  // Update handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate contract dates
    if (contractEndDate < contractStartDate) {
      toast.error("Contract end date cannot be before start date");
      return;
    }

    setIsLoading(true);
    try {
      let payload;

      // Calculate facilities with full objects
      const selectedFacilityObjects = selectedFacilities.map(id => {
        const facility = venueFacilities.find(f => f._id === id);
        return facility ? {
          id: facility._id,
          name: facility.name,
          logo: facility.logo
        } : null;
      }).filter(facility => facility !== null);

      if (isEditMode) {
        const changedFields = getChangedFields();
        console.log("Changed fields:", changedFields);

        if (Object.keys(changedFields).length === 0) {
          toast.info("No changes detected");
          setIsLoading(false);
          return;
        }

        payload = {
          ...changedFields,
          _id: facilityId,
          coverPhoto: images[0] || "", // Ensure cover photo is always included
          images: images.length > 1 ? images.slice(1) : [] // Include additional images if any
        };

      } else {

        payload = {
          name: facilityName.toLowerCase(),
          discount: 0,
          coverPhoto: images[0] || "", // First image as cover photo
          images: images.length > 1 ? images.slice(1) : [], // Rest as additional images
          location: {
            name: facilityLocation,
            lat: selectedPlace?.geometry?.location?.lat() || 24.8607,
            lng: selectedPlace?.geometry?.location?.lng() || 67.0011
          },
          email: email || "",
          phone: contactNumber,
          rating: 0,
          terms: termsAndConditions,
          socialLinks: {
            facebook: facebook || "",
            instagram: instagram || ""
          },
          startingFrom: startingPrice,
          venueTimings: venueTimings.map(timing => ({
            day: timing.day,
            startTime: formatTimeToAMPM(timing.from),
            endTime: formatTimeToAMPM(timing.to)
          })),
          contractTenure: {
            from: formatDateForPayload(contractStartDate),
            to: formatDateForPayload(contractEndDate)
          },
          venueFacility: selectedFacilityObjects,
          availableSport: [...(reduxSelectedSports.indoor || []), ...(reduxSelectedSports.outdoor || [])],
          bankDetails: {
            bankName: bankName || null,
            accountNumber: accountNumber || null,
            accountHolderName: accountHolderName || null,
            iban: iban || null,
            ntn: ntn || null
          }
        };
      }

      if (isEditMode) {
        // Handle edit mode
        const response = await EditNewFacility(payload);
        console.log("Edit API response:", response);

        if (response.status === 200 && response.data?.status === true) {
          // Close preview modal first
          setShowPreviewModal(false);
          resetFormData();
          toast.success("Facility updated successfully!");
          setTimeout(() => {
            navigate('/facilities');
            window.location.reload();
          }, 2000);
        } else {
          toast.error(response.data?.message || "Failed to update facility. Please try again.");
        }
      } else {
        // Handle add mode
        const result = await dispatch(addFacility(payload));
        // Check if the action was fulfilled and successful
        if (result.meta.requestStatus === 'fulfilled') {
          const responseData = result.payload;

          if (responseData && responseData.status === true) {
            // Close preview modal first
            setShowPreviewModal(false);
            resetFormData();
            toast.success("Facility has been created successfully and is currently inactive. It will remain under verification until contract tenure is added and approved by admin.!");
            setTimeout(() => {
              dispatch(resetFacilityState());
            }, 3000);
          } else {
            // API returned success: false
            toast.error(responseData?.message || "Failed to add facility. Please try again.");
            // Close modal on error for add mode only
            setShowPreviewModal(false);
          }
        } else {
          // Action was rejected
          const errorMessage = result.payload?.message || result.error?.message || "Failed to add facility. Please try again.";
          toast.error(errorMessage);
          // Close modal on error for add mode only
          setShowPreviewModal(false);
        }
      }
    } catch (error) {
      console.error('Failed to ' + (isEditMode ? 'update' : 'add') + ' facility:', error);
      toast.error(error.response?.data?.message || "An error occurred. Please try again.");
      // Close modal on error for add mode only
      if (!isEditMode) {
        setShowPreviewModal(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Update the SportSelectionModal component
  const SportSelectionModal = ({ show, onHide, onSelect, sportsData }) => {
    const dispatch = useDispatch();
    const activeTab = useSelector(state => state.sports.activeTab);
    const selectedSports = useSelector(state => state.sports.selectedSports);

    // Add temporary state to track selections within the modal
    const [tempSelectedSports, setTempSelectedSports] = useState({ indoor: [], outdoor: [] });

    // Initialize temporary selections when modal opens
    useEffect(() => {
      if (show) {
        setTempSelectedSports({
          indoor: [...selectedSports.indoor],
          outdoor: [...selectedSports.outdoor]
        });
      }
    }, [show, selectedSports]);

    const handleTabChange = (tab) => {
      dispatch(setActiveTab(tab));
    };

    const isSportSelected = (sportId, type) => {
      const category = type.toLowerCase();
      return tempSelectedSports[category].includes(sportId);
    };

    const handleSportClick = (sport) => {
      const type = sport.type || (activeTab === 'indoor' ? 'indoor' : 'outdoor');
      const category = type.toLowerCase();

      setTempSelectedSports(prev => ({
        ...prev,
        [category]: prev[category].includes(sport._id)
          ? prev[category].filter(id => id !== sport._id)
          : [...prev[category], sport._id]
      }));
    };

    const handleSave = () => {
      // Apply temporary selections to the main state
      dispatch(clearSelectedSports());
      tempSelectedSports.indoor.forEach(sportId => {
        dispatch(selectSport({ sportId, type: 'indoor' }));
      });
      tempSelectedSports.outdoor.forEach(sportId => {
        dispatch(selectSport({ sportId, type: 'outdoor' }));
      });
      onHide();
    };

    const handleCancel = () => {
      // Reset temporary selections to original state
      setTempSelectedSports({
        indoor: [...selectedSports.indoor],
        outdoor: [...selectedSports.outdoor]
      });
      onHide();
    };

    return (
      <Modal show={show} onHide={handleCancel} centered size="lg" className="sports-modal">
        <Modal.Header closeButton>
          <Modal.Title>Select Sports</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sportsLoading ? (
            <div className="loading-container">
              <CircularProgress size={40} style={{ color: '#38B000' }} />
              <p className="loading-text">Loading sports data...</p>
            </div>
          ) : !sportsData || (!sportsData.indoor?.length && !sportsData.outdoor?.length) ? (
            <div className="error-container">
              <p className="error-message">No sports data available</p>
              <button className="retry-button" onClick={handleRetrySportsFetch} disabled={isReadOnly}>
                Retry
              </button>
            </div>
          ) : (
            <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
              <Nav variant="tabs">
                <Nav.Item>
                  <Nav.Link eventKey="indoor" className="tabtext">Indoor Sports</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="outdoor" className="tabtext">Outdoor Sports</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="indoor">
                  <div className="sport-list">
                    {sportsData.indoor?.map((sport) => (
                      <div
                        key={sport._id}
                        className={`sport-item ${isSportSelected(sport._id, 'indoor') ? 'selected-sport-item' : ''}`}
                        onClick={() => !isReadOnly && handleSportClick(sport)}
                      >
                        <img
                          src={sportIcons[sport.name] || sportIcons.default}
                          alt={sport.name}
                          className="sport-icon"
                        />
                        <span className="sport-name">{sport.name}</span>
                        <span className="sport-check">✓</span>
                      </div>
                    ))}
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="outdoor">
                  <div className="sport-list">
                    {sportsData.outdoor?.map((sport) => (
                      <div
                        key={sport._id}
                        className={`sport-item ${isSportSelected(sport._id, 'outdoor') ? 'selected-sport-item' : ''}`}
                        onClick={() => !isReadOnly && handleSportClick(sport)}
                      >
                        <img
                          src={sportIcons[sport.name] || sportIcons.default}
                          alt={sport.name}
                          className="sport-icon"
                        />
                        <span className="sport-name">{sport.name}</span>
                        <span className="sport-check">✓</span>
                      </div>
                    ))}
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-cancel" onClick={handleCancel} disabled={isReadOnly}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSave} disabled={isReadOnly}>
            Save Selection
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  // Preview Modal component
  const PreviewModal = ({ show, onHide, payload, onSubmit, isEditMode }) => {
    useEffect(() => {
      if (show && payload) {
        console.log('Preview Payload:', payload);
      }
    }, [show, payload]);

    const getSportNames = () => {
      if (!payload?.availableSport || !sports) return [];

      const allSports = [...(sports.indoor || []), ...(sports.outdoor || [])];
      const selectedSports = Array.isArray(payload.availableSport)
        ? payload.availableSport
        : [...(payload.availableSport?.indoor || []), ...(payload.availableSport?.outdoor || [])];

      // Handle case when no sports are selected
      if (!selectedSports || selectedSports.length === 0) {
        return [];
      }

      return selectedSports.map(sportId => {
        const sport = allSports.find(s => s._id === sportId);
        return sport ? sport.name : 'Unknown Sport';
      }).filter(name => name !== 'Unknown Sport'); // Filter out any invalid sports
    };

    const getImageUrl = (imageUrl) => {
      if (!imageUrl) return null;
      if (imageUrl.startsWith('http')) return imageUrl;
      if (imageUrl.startsWith('/facility')) {
        return `https://goplayupdated.s3.us-east-1.amazonaws.com${imageUrl}`;
      }
      return imageUrl;
    };

    const sportNames = getSportNames();

    const hasFieldChanged = (fieldName) => {
      return isEditMode &&
        payload?.changedFields &&
        (payload.changedFields[fieldName] !== undefined ||
          (fieldName.includes('.') && payload.changedFields[fieldName.split('.')[0]] !== undefined));
    };

    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? "Preview Changes" : "Preview Facility Data"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="preview-container">
            <h5 className="mb-3">Facility Information</h5>
            {isEditMode && (
              <div className="alert alert-info mb-3">
                {Object.keys(payload?.changedFields || {}).length === 0
                  ? "No changes detected."
                  : "Changed fields are highlighted in yellow."}
              </div>
            )}

            {/* Images Preview Section */}
            <div className={`preview-section ${hasFieldChanged('images') || hasFieldChanged('coverPhoto') ? 'changed-section' : ''}`}>
              <h6>
                Facility Images
                {(hasFieldChanged('images') || hasFieldChanged('coverPhoto')) &&
                  <span className="changed-badge">Changed</span>
                }
              </h6>
              <div className="images-preview-grid">
                {payload?.images?.map((image, index) => (
                  <div key={index} className={`image-preview-item ${index === 0 ? 'cover-photo' : ''}`}>
                    <img src={getImageUrl(image)} alt={`Facility ${index + 1}`} />
                    {index === 0 && <span className="cover-photo-badge">Cover Photo</span>}
                  </div>
                ))}
              </div>
              {(!payload?.images || payload.images.length === 0) && (
                <p className="text-muted">No images uploaded</p>
              )}
            </div>

            <div className="preview-section">
              <h6>Basic Info</h6>
              <p className={hasFieldChanged('name') ? 'changed-field' : ''}>
                <strong>Name:</strong> {payload?.name}
                {hasFieldChanged('name') && <span className="changed-badge">Changed</span>}
              </p>
              <p className={hasFieldChanged('location') ? 'changed-field' : ''}>
                <strong>Location:</strong> {payload?.location?.name}
                {hasFieldChanged('location') && <span className="changed-badge">Changed</span>}
              </p>
              <p className={hasFieldChanged('phone') ? 'changed-field' : ''}>
                <strong>Phone:</strong> {payload?.phone}
                {hasFieldChanged('phone') && <span className="changed-badge">Changed</span>}
              </p>
              <p className={hasFieldChanged('email') ? 'changed-field' : ''}>
                <strong>Email:</strong> {payload?.email || 'Not provided'}
                {hasFieldChanged('email') && <span className="changed-badge">Changed</span>}
              </p>
              <p className={hasFieldChanged('startingFrom') ? 'changed-field' : ''}>
                <strong>Starting Price:</strong> PKR {payload?.startingFrom}
                {hasFieldChanged('startingFrom') && <span className="changed-badge">Changed</span>}
              </p>
            </div>

            {/* Contract Tenure section removed - managed by admin only */}

            <div className={`preview-section ${hasFieldChanged('venueFacility') ? 'changed-section' : ''}`}>
              <h6>
                Venue Facilities
                {hasFieldChanged('venueFacility') && <span className="changed-badge">Changed</span>}
              </h6>
              <ul>
                {payload?.venueFacility?.map((facility, index) => {
                  const facilityName = typeof facility === 'string' ? facility : facility.name;
                  const facilityLogo = typeof facility === 'object' ? facility.logo : null;
                  const isChanged = hasFieldChanged('venueFacility');

                  return (
                    <li key={index} className={isChanged ? 'changed-field' : ''}>
                      <div className="d-flex align-items-center">
                        <span>{facilityName}</span>
                        {facilityLogo && (
                          <img
                            src={`https://goplayupdated.s3.us-east-1.amazonaws.com${facilityLogo}`}
                            alt={facilityName}
                            style={isFacilitySelected(facility) ? { filter: 'invert(100%) sepia(100%) saturate(0%) hue-rotate(271deg) brightness(105%) contrast(101%)' } : {}}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        {isChanged && <span className="changed-badge ms-2">Changed</span>}
                      </div>
                    </li>
                  );
                })}
                {(!payload?.venueFacility || payload.venueFacility.length === 0) && (
                  <li className="text-muted">No venue facilities selected</li>
                )}
              </ul>
            </div>

            <div className={`preview-section ${hasFieldChanged('venueTimings') ? 'changed-section' : ''}`}>
              <h6>
                Venue Timings
                {hasFieldChanged('venueTimings') && <span className="changed-badge">Changed</span>}
              </h6>
              <ul>
                {payload?.venueTimings?.map((timing, index) => (
                  <li key={index}>
                    {timing.day}: {timing.startTime} - {timing.endTime}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`preview-section ${hasFieldChanged('terms') ? 'changed-section' : ''}`}>
              <h6>
                Terms and Conditions
                {hasFieldChanged('terms') && <span className="changed-badge">Changed</span>}
              </h6>
              <p>{payload?.terms}</p>
            </div>

            <div className={`preview-section ${hasFieldChanged('availableSport') ? 'changed-section' : ''}`}>
              <h6>
                Available Sports
                {hasFieldChanged('availableSport') && <span className="changed-badge">Changed</span>}
              </h6>
              {sportNames.length > 0 ? (
                <ul>
                  {sportNames.map((sportName, index) => (
                    <li key={index}>{sportName}</li>
                  ))}
                </ul>
              ) : (
                <p>No sports selected</p>
              )}
            </div>

            <div className={`preview-section ${hasFieldChanged('bankDetails') ? 'changed-section' : ''}`}>
              <h6>
                Bank Details
                {hasFieldChanged('bankDetails') && <span className="changed-badge">Changed</span>}
              </h6>
              {payload?.bankDetails ? (
                <div>
                  {payload.bankDetails.bankName && (
                    <p className={hasFieldChanged('bankDetails') ? 'changed-field' : ''}>
                      <strong>Bank Name:</strong> {payload.bankDetails.bankName}
                    </p>
                  )}
                  {payload.bankDetails.accountNumber && (
                    <p className={hasFieldChanged('bankDetails') ? 'changed-field' : ''}>
                      <strong>Account Number:</strong> {payload.bankDetails.accountNumber}
                    </p>
                  )}
                  {payload.bankDetails.accountHolderName && (
                    <p className={hasFieldChanged('bankDetails') ? 'changed-field' : ''}>
                      <strong>Account Holder Name:</strong> {payload.bankDetails.accountHolderName}
                    </p>
                  )}
                  {payload.bankDetails.iban && (
                    <p className={hasFieldChanged('bankDetails') ? 'changed-field' : ''}>
                      <strong>IBAN:</strong> {payload.bankDetails.iban}
                    </p>
                  )}
                  {payload.bankDetails.ntn && (
                    <p className={hasFieldChanged('bankDetails') ? 'changed-field' : ''}>
                      <strong>NTN:</strong> {payload.bankDetails.ntn}
                    </p>
                  )}
                  {!payload.bankDetails.bankName && !payload.bankDetails.accountNumber &&
                    !payload.bankDetails.accountHolderName && !payload.bankDetails.iban &&
                    !payload.bankDetails.ntn && (
                      <p className="text-muted">No bank details provided</p>
                    )}
                </div>
              ) : (
                <p className="text-muted">No bank details provided</p>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={(isEditMode && Object.keys(payload?.changedFields || {}).length === 0) || isReadOnly}
          >
            {isEditMode ? "Update" : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // Parse dates from various formats to Date objects
  const parseDate = (dateString) => {
    if (!dateString) return new Date();

    try {
      // Check if already a date object
      if (dateString instanceof Date) {
        return dateString;
      }

      // Handle different date formats
      const parts = dateString.split('-').map(num => parseInt(num, 10));

      if (parts.length === 3) {
        // Check if it's YYYY-MM-DD format (ISO format)
        if (parts[0] > 12) {
          // First part is year (YYYY-MM-DD)
          const [year, month, day] = parts;
          return new Date(year, month - 1, day);
        } else {
          // First part is month (MM-DD-YYYY)
          const [month, day, year] = parts;
          return new Date(year, month - 1, day);
        }
      }

      // Fallback to direct Date parsing
      return new Date(dateString);
    } catch (error) {
      console.error('Error parsing date:', error, dateString);
      return new Date();
    }
  };

  console.log("facilityData", facilityData);

  // Add useEffect to handle loading contract dates in edit mode
  useEffect(() => {
    if (isEditMode && facilityData) {
      // Set contract dates
      if (facilityData.contractTenure) {
        console.log("Loading contract tenure:", facilityData.contractTenure);

        if (facilityData.contractTenure.from) {
          const startDate = parseDate(facilityData.contractTenure.from);
          console.log("Setting contract start date:", startDate);
          setContractStartDate(startDate);
        }

        if (facilityData.contractTenure.to) {
          const endDate = parseDate(facilityData.contractTenure.to);
          console.log("Setting contract end date:", endDate);
          setContractEndDate(endDate);
        }
      }
    }
  }, [isEditMode, facilityData]);

  // Add useEffect for loading venue timings in edit mode
  useEffect(() => {
    if (isEditMode && facilityData?.venueTimings && Array.isArray(facilityData.venueTimings)) {
      console.log("Loading venue timings in edit mode:", facilityData.venueTimings);

      const formattedTimings = facilityData.venueTimings.map(timing => {
        // Handle both possible formats of time data
        const startTime = timing.startTime || timing.from;
        const endTime = timing.endTime || timing.to;

        return {
          day: timing.day || "",
          from: formatTimeFrom12To24(startTime) || "",
          to: formatTimeFrom12To24(endTime) || ""
        };
      });

      console.log("Formatted venue timings:", formattedTimings);

      // Only update if we have valid timings
      if (formattedTimings.length > 0) {
        setVenueTimings(formattedTimings);
      }
    }
  }, [isEditMode, facilityData]);

  // Add back the retry function for fetching sports
  const handleRetrySportsFetch = () => {
    console.log("Retrying sports fetch");
    dispatch(fetchSports());
  };

  // Add back the remove sport handler
  const handleRemoveSport = ({ sportId, type }) => {
    if (isReadOnly) {
      console.log("Cannot remove sport in read-only mode");
      return;
    }
    console.log("Removing sport:", sportId, "of type:", type);
    dispatch(removeSport({ sportId, type }));
  };

  // Add back the handlePreview function
  const handlePreview = () => {
    // Calculate facilities with full objects for preview
    const selectedFacilityObjects = selectedFacilities.map(id => {
      const facility = venueFacilities.find(f => f._id === id);
      return facility ? {
        id: facility._id,
        name: facility.name,
        logo: facility.logo
      } : null;
    }).filter(facility => facility !== null);

    // Prepare the complete facility data for preview
    const completeData = {
      id: facilityId,
      name: facilityName.toLowerCase(),
      discount: 0,
      coverPhoto: images[0] || '', // First image as cover photo
      images: images, // All images array
      location: {
        name: facilityLocation,
        lat: selectedPlace?.geometry?.location?.lat() || 24.8607,
        lng: selectedPlace?.geometry?.location?.lng() || 67.0011
      },
      email: email || "",
      phone: contactNumber,
      rating: 0,
      terms: termsAndConditions,
      socialLinks: {
        facebook: facebook || "",
        instagram: instagram || ""
      },
      startingFrom: startingPrice,
      venueTimings: venueTimings.map(timing => ({
        day: timing.day,
        startTime: formatTimeToAMPM(timing.from),
        endTime: formatTimeToAMPM(timing.to)
      })),
      // Contract tenure removed from preview - managed by admin only
      venueFacility: selectedFacilityObjects,
      availableSport: [...reduxSelectedSports.indoor, ...reduxSelectedSports.outdoor],
      bankDetails: {
        bankName: bankName || null,
        accountNumber: accountNumber || null,
        accountHolderName: accountHolderName || null,
        iban: iban || null,
        ntn: ntn || null
      }
    };

    // If in edit mode, calculate changed fields
    if (isEditMode) {
      const changedFields = getChangedFields();
      completeData.changedFields = changedFields;
    }

    setPreviewPayload(completeData);
    setShowPreviewModal(true);
  };

  // Update the image upload section in the JSX
  const renderImageUploadSection = () => (
    <Col lg={12} className="facilityCol">
      <h5 className="h5headings">Upload Images*</h5>
      <Col lg={12} className="upload-container">
        <div className="image-preview-container">
          {/* Cover Photo */}
          {images[0] && (
            <div className="image-wrapper cover-photo">
              <img src={images[0]} alt="cover" className="uploaded-image" />
              <div className="cover-photo-label">Cover Photo</div>
              <label className="replace-cover-btn">
                <input
                  type="file"
                  accept="image/*"
                  onChange={isReadOnly ? undefined : handleImageUpload}
                  disabled={isReadOnly}
                  hidden
                  data-type="cover"
                />
                Replace
              </label>
            </div>
          )}

          {/* Additional Images */}
          {images.slice(1).map((image, index) => (
            <div key={index + 1} className="image-wrapper">
              <img src={image} alt={`upload-${index + 1}`} className="uploaded-image" />
              <button
                onClick={() => !isReadOnly && removeImage(index + 1)}
                className="remove-btn"
              >
                ✖
              </button>
            </div>
          ))}

          {/* Upload Placeholder */}
          {images.length < 5 && (
            <label className="upload-placeholder">
              <input
                type="file"
                multiple={images.length > 0}
                accept="image/*"
                onChange={handleImageUpload}
                hidden
                data-type="additional"
              />
              <div className="upload-box">
                <p>{images.length === 0 ? "Upload cover photo" : "Upload additional images"}</p>
                <p className="upload-limit">(less than 5mb)</p>
              </div>
            </label>
          )}
        </div>
      </Col>
    </Col>
  );

  // Add styles for the replace cover button
  const additionalStyles = `
    .replace-cover-btn {
      position: absolute;
      bottom: 10px;
      right: 10px;
      background-color: rgba(40, 167, 69, 0.8);
      color: white;
      padding: 5px 10px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      transition: background-color 0.2s;
    }

    .replace-cover-btn:hover {
      background-color: rgba(40, 167, 69, 1);
    }

    .image-wrapper.cover-photo {
      position: relative;
      border: 2px solid #28a745;
    }
  `;

  const facilityIcons = {
    'Password': <FaKey />,
    'Equipment Provided': <FaDumbbell />,
    'Refreshment': <FaCoffee />,
    'Lockers': <FaLock />,
    'Toilet': <FaToilet />,
    'Shower': <FaShower />,
    'Parking': <FaParking />,
    'Internet': <FaWifi />
  };

  return (
    <>
      <Container fluid>
        <h1 className="container-title">{isReadOnly ? "View Facility" : (isEditMode ? "Edit Facility" : "Add Facility")}</h1>
        <Row>
          <div className='input-form-container'>
            {/* Basic Info Section */}
            <Row>
              <h5 className='h5headings'>Basic Info</h5>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Facility Name*</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={facilityName}
                    onChange={(e) => setFacilityName(e.target.value)}
                    placeholder="Facility name"
                    disabled={(isEditMode && !EDITABLE_FIELDS.name) || isReadOnly}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Facility Location*</label>
                  <PlacesAutocomplete
                    value={facilityLocation}
                    onChange={setFacilityLocation}
                    placeholder="Enter facility location"
                    disabled={(isEditMode && !EDITABLE_FIELDS.location) || isReadOnly}
                    onPlaceSelect={handlePlaceSelect}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Contact Number*</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    placeholder="+92_________"
                    disabled={(isEditMode && !EDITABLE_FIELDS.phone) || isReadOnly}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Email</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    disabled={(isEditMode && !EDITABLE_FIELDS.email) || isReadOnly}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Facebook</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={facebook}
                    onChange={(e) => setFacebook(e.target.value)}
                    placeholder="Facebook"
                    disabled={(isEditMode && !EDITABLE_FIELDS.socialLinks) || isReadOnly}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Instagram</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                    placeholder="Instagram"
                    disabled={(isEditMode && !EDITABLE_FIELDS.socialLinks) || isReadOnly}
                  />
                </div>
              </Col>
            </Row>

            {/* Upload Image Section */}
            {renderImageUploadSection()}

            {/* Venue Timings Section */}
            <h5 className='h5headings'>Venue Timings*</h5>
            {venueTimings.map((timing, index) => (
              <div className="input-container venue position-relative" key={index}>
                <div className="timing-input-group">
                  <div className="timing-field">
                    <label className="input-label">Day</label>
                    <select
                      className="custom-input"
                      value={timing.day}
                      onChange={(e) => handleTimingChange(index, "day", e.target.value)}
                      disabled={(isEditMode && !EDITABLE_FIELDS.venueTimings) || isReadOnly}
                    >
                      <option value="">Select day</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Sunday">Sunday</option>
                    </select>
                  </div>
                  <div className="timing-field">
                    <label className="input-label">Opening Time</label>
                    <input
                      type="time"
                      className="custom-input"
                      value={timing.from}
                      onChange={(e) => handleTimingChange(index, "from", e.target.value)}
                      placeholder="From"
                      disabled={(isEditMode && !EDITABLE_FIELDS.venueTimings) || isReadOnly}
                    />
                  </div>
                  <div className="timing-field">
                    <label className="input-label">Closing Time</label>
                    <input
                      type="time"
                      className="custom-input"
                      value={timing.to}
                      onChange={(e) => handleTimingChange(index, "to", e.target.value)}
                      placeholder="To"
                      disabled={(isEditMode && !EDITABLE_FIELDS.venueTimings) || isReadOnly}
                    />
                  </div>
                </div>
                {venueTimings.length > 1 && EDITABLE_FIELDS.venueTimings && (
                  <button
                    className="remove-timing-btn"
                    onClick={() => removeVenueTiming(index)}
                    title="Remove timing"
                    disabled={(isEditMode && !EDITABLE_FIELDS.venueTimings) || isReadOnly}
                  >
                    ✖
                  </button>
                )}
              </div>
            ))}

            {/* Add styles for venue timing inputs */}
            <style jsx>{`
              .timing-input-group {
                display: flex;
                gap: 15px;
                width: 100%;
              }

              .timing-field {
                flex: 1;
              }

              .input-container.venue {
                margin-bottom: 20px;
              }

              /* Ensure consistent height for time inputs */
              input[type="time"].custom-input {
                min-height: 40px;
              }
            `}</style>

            {EDITABLE_FIELDS.venueTimings && (
              <Col lg={12} className='addfacilityopt' onClick={() => !isReadOnly && addVenueTiming()}>
                <img src={plus} className='addfacilityicon' alt="Add" />
                <p className='venuetiming m-0'>Add Venue Timing</p>
              </Col>
            )}

            {/* Contract Tenure Section - Only show in edit mode, not in add mode */}
            {isEditMode && (
              <>
                <h5 className='h5headings'>Contract Tenure (Read Only)</h5>
                <Row className="contract-tenure-row">
                  <Col lg={6}>
                    <div className="tenure-input-wrapper">
                      <label className="tenure-label">Start Date</label>
                      <div className="date-picker-container">
                        <DatePicker
                          selected={contractStartDate}
                          onChange={(date) => setContractStartDate(date)}
                          disabled={true}
                          placeholderText="N/A"
                          customInput={
                            <Form.Control
                              type="text"
                              readOnly
                              className="date-input"
                              placeholder="N/A"
                              value={contractStartDate ? contractStartDate.toLocaleDateString('en-GB') : "N/A"}
                            />
                          }
                        />
                        <img
                          src={CalenderIcon}
                          alt="Calendar"
                          className="calendar-icon disabled"
                        />
                      </div>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="tenure-input-wrapper">
                      <label className="tenure-label">End Date</label>
                      <div className="date-picker-container">
                        <DatePicker
                          selected={contractEndDate}
                          onChange={handleContractEndDateChange}
                          disabled={true}
                          placeholderText="N/A"
                          customInput={
                            <Form.Control
                              type="text"
                              readOnly
                              className="date-input"
                              placeholder="N/A"
                              value={contractEndDate ? contractEndDate.toLocaleDateString('en-GB') : "N/A"}
                            />
                          }
                        />
                        <img
                          src={CalenderIcon}
                          alt="Calendar"
                          className="calendar-icon disabled"
                        />
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Add styles for contract tenure section */}
                <style jsx>{`
                  .contract-tenure-row {
                    margin-bottom: 24px;
                  }

                  .tenure-input-wrapper {
                    width: 100%;
                  }

                  .tenure-label {
                    display: block;
                    margin-bottom: 8px;
                    font-size: 16px;
                    color: #333;
                    font-weight: 500;
                  }

                  .date-picker-container {
                    position: relative;
                    width: 100%;
                  }

                  .date-input {
                    width: 100%;
                    height: 45px;
                    padding: 8px 40px 8px 12px;
                    border: 1px solid #E0E0E0;
                    border-radius: 4px;
                    background-color: #F8F9FA;
                    font-size: 14px;
                    color: #333;
                  }

                  .date-input:hover {
                    border-color: #BDBDBD;
                  }

                  .date-input:focus {
                    border-color: #28a745;
                    outline: none;
                    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
                  }

                  .calendar-icon {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    cursor: pointer;
                    width: 20px;
                    height: 20px;
                    opacity: 0.6;
                    transition: opacity 0.2s;
                  }

                  .calendar-icon:hover {
                    opacity: 1;
                  }

                  .calendar-icon.disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                  }

                  .date-input::placeholder {
                    color: #757575;
                  }
                `}</style>
              </>
            )}

            {/* Starting Price Section */}
            <h5 className='h5headings'>Starting Price*</h5>
            <div className="input-container">
              <input
                type="number"
                className="custom-input"
                value={startingPrice}
                onChange={(e) => setStartingPrice(e.target.value)}
                placeholder="Starting price"
                disabled={(isEditMode && !EDITABLE_FIELDS.startingFrom) || isReadOnly}
              />
            </div>

            {/* Terms and Conditions Section */}
            <h5 className='h5headings'>Terms and Conditions*</h5>
            <div className="input-container">
              <input
                className="custom-input"
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                placeholder="Terms and conditions"
                disabled={(isEditMode && !EDITABLE_FIELDS.terms) || isReadOnly}
              />
            </div>

            {/* Bank Details Section */}
            <h5 className='h5headings'>Bank Details</h5>
            <Row>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Bank Name</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="Bank name"
                    disabled={(isEditMode && !EDITABLE_FIELDS.bankDetails) || isReadOnly}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Account Number</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="Account number"
                    disabled={(isEditMode && !EDITABLE_FIELDS.bankDetails) || isReadOnly}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">Account Holder Name</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                    placeholder="Account holder name"
                    disabled={(isEditMode && !EDITABLE_FIELDS.bankDetails) || isReadOnly}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">IBAN</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    placeholder="IBAN"
                    disabled={(isEditMode && !EDITABLE_FIELDS.bankDetails) || isReadOnly}
                  />
                </div>
              </Col>
              <Col sm={6}>
                <div className="input-container">
                  <label className="input-label">NTN</label>
                  <input
                    type="text"
                    className="custom-input af"
                    value={ntn}
                    onChange={(e) => setNtn(e.target.value)}
                    placeholder="NTN"
                    disabled={(isEditMode && !EDITABLE_FIELDS.bankDetails) || isReadOnly}
                  />
                </div>
              </Col>
            </Row>

            {/* Venue Facilities Section */}
            <h5 className='h5headings'>Venue Facilities*</h5>
            {facilitiesLoading ? (
              <Row>
                <Col xs={12} className="text-center py-4">
                  <CircularProgress />
                </Col>
              </Row>
            ) : (
              <div className="venue-facilities-tags">
                {venueFacilities.map((facility) => (
                  <button
                    key={facility._id}
                    className={`facility-tag ${isFacilitySelected(facility) ? 'selected' : ''} ${isEditMode && !EDITABLE_FIELDS.venueFacility ? 'disabled' : ''}`}
                    onClick={() => EDITABLE_FIELDS.venueFacility && !isReadOnly && handleSelect(facility._id)}
                  >
                    <span className="facility-icon">{facilityIcons[facility.name] || <FaDumbbell />}</span>
                    {facility.name}
                  </button>
                ))}
              </div>
            )}

            {/* Display for String-based facilities if any are preloaded */}
            {selectedFacilityNames.length > 0 && (
              <div className="mt-3 venuee">
                <h6>Selected Facilities:</h6>
                <div className="d-flex flex-wrap">
                  {selectedFacilityNames.map((name, index) => (
                    <div key={index} className="selected-facility-badge m-1">
                      {name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Sports Section */}
            <h5 className='h5headings'>Available Sport*</h5>
            <div className="sport-selector-container">
              {EDITABLE_FIELDS.availableSport && (
                <div
                  className="addfacilityopt"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isReadOnly) {
                      console.log("Sports modal button clicked");
                      handleOpenSportModal();
                    }
                  }}
                  style={{ cursor: (EDITABLE_FIELDS.availableSport && !isReadOnly) ? 'pointer' : 'not-allowed' }}
                >
                  <img src={plus} className="addfacilityicon" alt="Add" />
                  <p className='venuetiming m-0'>Add or Remove Sports</p>
                </div>
              )}

              {/* Selected Sports Display */}
              <Row className="mt-3 sportbadge">
                {[...(reduxSelectedSports.indoor || []), ...(reduxSelectedSports.outdoor || [])].map((sportId) => {
                  const sport = [...(sports.indoor || []), ...(sports.outdoor || [])].find(s => s._id === sportId);
                  return sport ? (
                    <Col key={sportId} xs={6} md={4} lg={2} className="mb-2">
                      <div className="selected-sport-badge">
                        <div className="sport-badge-content">
                          <img
                            src={sportIcons[sport.name] || sportIcons.default}
                            className="sport-badge-icon"
                            alt={sport.name}
                          />
                          <span className="badge-sport-name">{sport.name}</span>
                        </div>
                        {EDITABLE_FIELDS.availableSport && (
                          <span
                            className="remove-sport"
                            style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isReadOnly) {
                                handleRemoveSport({ sportId, type: sport.type });
                              }
                            }}
                          >
                            <img src={cross} alt="Remove" />
                          </span>
                        )}
                      </div>
                    </Col>
                  ) : null;
                })}
              </Row>

              {/* Sport Selection Modal */}
              {isModalOpen && (
                <SportSelectionModal
                  show={isModalOpen}
                  onHide={() => dispatch(closeModal())}
                  onSelect={handleSelectSport}
                  sportsData={sports}
                />
              )}
            </div>

            {/* Submit Button */}
            <Col lg={12} className='facilityBtnCol'>
              <button
                className="btn green addfacility"
                onClick={handlePreview}
                disabled={isReadOnly || !isFormValid() || addFacilityLoading}
              >
                {addFacilityLoading ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <CircularProgress size={24} style={{ color: '#fff', marginRight: '8px' }} />
                    Preview
                  </div>
                ) : 'Preview'}
              </button>
            </Col>
          </div>
        </Row>

        {/* Add error display if needed */}
        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}

        {/* Add success message if needed */}
        {success && (
          <div className="alert alert-success mt-3">
            Facility {isEditMode ? "updated" : "added"} successfully!
          </div>
        )}
      </Container>

      {/* Additional CSS for the Sport Selection feature */}
      <style jsx>{`
        .sport-list {
          max-height: 300px;
          overflow-y: auto;
        }
        
        .sport-item {
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .sport-item:hover {
          background-color: #f5f5f5;
        }
        
        .selected-sport-item {
          background-color: #E8F5E9;
        }
        
        .selected-sport-badge {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: #E8F5E9;
          padding: 8px 12px;
          border-radius: 20px;
          font-size: 14px;
        }
        
        .remove-sport {
          margin-left: 8px;
          cursor: pointer;
          color: red;
          font-weight: bold;
        }
        
        .selected-facility-badge {
          display: inline-flex;
          align-items: center;
          background-color: #E8F5E9;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 14px;
          color: #2E7D32;
        }
        
        .remove-facility-btn {
          border: none;
          background: none;
          color: #e74c3c;
          font-size: 12px;
          cursor: pointer;
          padding: 0;
          margin-left: 8px;
        }
        
        .remove-facility-btn:hover {
          color: #c0392b;
        }

        .alert {
          margin-top: 1rem;
          padding: 1rem;
          border-radius: 4px;
        }

        .alert-success {
          background-color: #d4edda;
          border-color: #c3e6cb;
          color: #155724;
        }

        .alert-danger {
          background-color: #f8d7da;
          border-color: #f5c6cb;
          color: #721c24;
        }
        
        .remove-timing-btn {
          position: absolute;
          right: -35px;
          top: 50%;
          transform: translateY(-50%);
          background-color: #f8d7da;
          color: #721c24;
          border: none;
          border-radius: 50%;
          width: 25px;
          height: 25px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s;
        }
        
        .remove-timing-btn:hover {
          background-color: #e74c3c;
          color: white;
        }
        
        .input-container.venue {
          position: relative;
        }
      `}</style>

      {/* Add this at the end of your JSX, just before the final closing tag */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <div className="loader">
            <div className="spinner"></div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        payload={previewPayload}
        onSubmit={handleSubmit}
        isEditMode={isEditMode}
      />

      {/* Add CSS for the preview modal */}
      <style jsx>{`
        .preview-container {
          max-height: 70vh;
          overflow-y: auto;
          padding: 10px;
        }
        
        .preview-section {
          margin-bottom: 20px;
          padding: 10px;
          border: 1px solid #eee;
          border-radius: 5px;
        }
        
        .preview-section h6 {
          margin-bottom: 10px;
          color: #28a745;
          font-weight: bold;
        }
        
        .json-preview {
          background-color: #f8f9fa;
          padding: 10px;
          border-radius: 5px;
          overflow-x: auto;
          font-size: 12px;
          max-height: 200px;
        }

        /* Image Preview Styles */
        .images-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 10px;
          margin-top: 10px;
        }

        .image-preview-item {
          position: relative;
          aspect-ratio: 1;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #ddd;
        }

        .image-preview-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-preview-item.cover-photo {
          grid-column: span 1;
          grid-row: span 1;
        }

        .cover-photo-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          background-color: rgba(40, 167, 69, 0.9);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }

        /* Venue Facilities Styles */
        .preview-section ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .preview-section ul li {
          display: flex;
          align-items: center;
          background-color: #f8f9fa;
          padding: 8px 12px;
          border-radius: 20px;
          font-size: 14px;
          border: 1px solid #e9ecef;
        }

        .preview-section ul li img {
          width: 20px;
          height: 20px;
          margin-left: 8px;
          object-fit: contain;
        }
      `}</style>

      {/* Add additional CSS for highlighting changed fields in preview */}
      <style jsx>{`
        .changed-field {
          background-color: #fff8e1;
          padding: 5px;
          border-radius: 3px;
        }
        
        .changed-section {
          background-color: #fff8e1;
        }
        
        .changed-badge {
          display: inline-block;
          margin-left: 8px;
          padding: 2px 6px;
          font-size: 10px;
          font-weight: bold;
          color: white;
          background-color: #ff9800;
          border-radius: 10px;
        }
      `}</style>

      {/* Add styles for disabled state */}
      <style jsx>{`
        .disabled {
          opacity: 0.6;
          cursor: not-allowed !important;
        }
        
        .facility-card.disabled {
          pointer-events: none;
          opacity: 0.6;
        }
        
        .calendar-icon.disabled {
          pointer-events: none;
          opacity: 0.6;
        }
        
        input:disabled,
        select:disabled,
        button:disabled {
          background-color: #f5f5f5;
          cursor: not-allowed;
        }
      `}</style>

      {/* Add styles for cover photo */}
      <style jsx>{`
        .image-wrapper.cover-photo {
          position: relative;
          border: 2px solid #28a745;
        }

        .cover-photo-label {
          position: absolute;
          top: 10px;
          left: 10px;
          background-color: #28a745;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
          z-index: 2;
        }

        .upload-box {
          text-align: center;
        }

        .upload-limit {
          font-size: 12px;
          color: #666;
          margin-top: 4px;
        }

        .remove-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      {/* Add the new styles */}
      <style jsx>{additionalStyles}</style>

      {/* Add styles for input labels */}
      <style jsx>{`
        .input-label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .input-container {
          margin-bottom: 16px;
        }

        /* Adjust input padding to accommodate labels */
        .custom-input.af {
          margin-top: 4px;
        }
      `}</style>
    </>
  );
};

export default AddFacility;